import axiosInstance from '../../../core/api/axios';
import { IExercise } from '../../../core/models/exercise';
import { viewContent } from '../../../core/queries/accessControl';
import { useAccessControlStore } from '../../../core/stores/accessControlStore';
import { useExercisePageStore, IExercisesFilterList } from '../stores/exercisePageStore';
import { useExercisePrintStore } from '../stores/exercisePrintStore';
import { useExerciseStore } from '../stores/exercisesStore';
import { useNewSubjectStore } from '../stores/newSubjectStore';
import { organizeExerciseField } from '../utils/exerciseHelper';
import { hasExercisesLoaded } from '../utils/hasExercisesLoaded';

export async function saveExerciseAnswer(
  slug: string,
  answers: string[],
  correct: boolean,
  time: number
): Promise<IExercise> {
  try {
    const response = await axiosInstance.post(`/exercises/${slug}/answer`, {
      userAnswer: answers,
      correct,
      time,
    });

    return response.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getExercise(slug: string): Promise<IExercise> {
  try {
    const isFreeUser = useAccessControlStore.getState().isFreeUser;
    
    const response = await axiosInstance.get(`/exercises/${slug}`);
    const exercise = response.data;

    if(isFreeUser && exercise) {
      await viewContent(slug, exercise.id, 'exercise');
    }

    return response.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export const getExercisesRequest = async (
  module: string,
  operation: string,
  page: number
) => {
  const setFilters = useExercisePageStore.getState().setExercisesFilterList;
  const setFullExerciseFilterList =
    useExercisePageStore.getState().setFullExerciseFilterList;

  const favorite = useExerciseStore.getState().favorite;
  const unanswered = useExerciseStore.getState().unanswered;
  const wrongAnswer = useExerciseStore.getState().wrongAnswer;
  const rightAnswer = useExerciseStore.getState().rightAnswer;
  const difficulty = useExerciseStore.getState().difficulty;
  const selectedYear = useExerciseStore.getState().selectedYear;
  const institutionType = useExerciseStore.getState().institutionType;
  const trial = useExerciseStore.getState().trialOptions;
  const questions = useExerciseStore.getState().questionOptions;
  const selectedSubjects = useNewSubjectStore.getState().selectedSubjects;
  const isEnem = useExerciseStore.getState().isEnem;
  const setPage = useExerciseStore.getState().setPage;

  const listUrl = operation === 'list' ? '/' + operation : '';
  const url = `/subareas/${module}${listUrl}/exercises`;

  let exerciseType: string | null = null;
  if (isEnem.includes('enem') && isEnem.length === 1) exerciseType = 'enem';
  if (isEnem.includes('vestibulares') && isEnem.length === 1) exerciseType = 'vest';

  const { data } = await axiosInstance.post(url, {
    wrongAnswer: wrongAnswer || null,
    rightAnswer: rightAnswer || null,
    unanswered: unanswered || null,
    favorite: favorite || null,
    page: page ?? 1,
    difficulty: difficulty.length > 0 ? difficulty : null,
    questionOptions: questions.length > 0 ? questions : null,
    trialOptions: trial || null,
    selectedYear: selectedYear.length > 0 ? selectedYear : null,
    institutionType: institutionType.length > 0 ? institutionType : null,
    subject: selectedSubjects.length > 0 ? selectedSubjects : null,
    exerciseType,
  });

  setPage(page);

  const snapshot: IExercisesFilterList = {
    page,
    module,
    favorite,
    unanswered,
    wrongAnswer,
    rightAnswer,
    difficulty,
    trial,
    questions,
    selectedYear,
    selectedSubjects,
    isEnem,
    institutionType,
  };

  if (operation !== 'list') setFilters(snapshot);
  else setFullExerciseFilterList(snapshot);

  return data;
};

const PAGE_SIZE = 20;

export async function getExercises(
  module: string,
  operation: string,
  page: number
): Promise<{ returnedData: IExercise[]; hasMore: boolean; totalExercises: number | null }> {
  try {
    const storedExercises = useExercisePageStore.getState().exercises;
    const exercisesFilterList = useExercisePageStore.getState().exercisesFilterList;
    const setStoreExercises = useExercisePageStore.getState().setExercises;
    const setTotalExercises = useExerciseStore.getState().setTotalExercises;

    const cacheHit = hasExercisesLoaded(module, 'List');
    const shouldLoadFromStore = cacheHit && storedExercises.length > 0;
    const shouldAppendData =
      cacheHit && exercisesFilterList != null && page > exercisesFilterList.page;

    if (shouldLoadFromStore && !shouldAppendData) {
      return {
        returnedData: storedExercises,
        hasMore: true,
        totalExercises: useExerciseStore.getState().totalExercises,
      };
    }

    const data = await getExercisesRequest(module, operation, page);
    const incoming: IExercise[] = data.exercises ?? [];

    const returnedData = shouldAppendData
      ? [...storedExercises, ...incoming]
      : incoming;

    const hasMore = incoming.length === PAGE_SIZE;

    if (typeof data.totalExercises === 'number') {
      setTotalExercises(data.totalExercises);
    }
    setStoreExercises(returnedData);

    return { returnedData, hasMore, totalExercises: data.totalExercises ?? null };
  } catch (e: any) {
    throw new Error(e);
  }
}

const processExercise = async (
  exercises: IExercise[],
  slug: string
): Promise<IExercise[]> => {
  const currentIndex = exercises.findIndex((it) => it.slug === slug);
  if (currentIndex < 0) return exercises;

  const current = exercises[currentIndex];
  if (current.alternatives && current.alternatives.length > 0) {
    exercises[currentIndex] = organizeExerciseField(current);
    return exercises;
  }

  const nextCached = useExercisePageStore.getState().nextExercise;
  const previousCached = useExercisePageStore.getState().previousExercise;

  if (nextCached && nextCached.slug === slug && nextCached.alternatives) {
    exercises[currentIndex] = organizeExerciseField(nextCached);
    return exercises;
  }
  if (previousCached && previousCached.slug === slug && previousCached.alternatives) {
    exercises[currentIndex] = organizeExerciseField(previousCached);
    return exercises;
  }

  const detail = await getExercise(slug);
  exercises[currentIndex] = organizeExerciseField(detail);

  return exercises;
};

export async function getExercisesList(
  module: string,
  slug: string
): Promise<IExercise[]> {
  try {
    const fullExerciseList = useExercisePageStore.getState().fullExerciseList;
    const setFullExerciseList = useExercisePageStore.getState().setFullExerciseList;

    const shouldLoadFromStore =
      hasExercisesLoaded(module, 'Full') && fullExerciseList.length > 0;

    let data: IExercise[] = shouldLoadFromStore
      ? fullExerciseList
      : (await getExercisesRequest(module, 'list', 1)).exercises ?? [];

    if (!shouldLoadFromStore) {
      setFullExerciseList(data);
    }

    data = await processExercise(data, slug);

    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getExercisesPrint(subarea: string) {
  try {
    const setLoading = useExercisePrintStore.getState().setIsLoading;
    const setExercises = useExercisePrintStore.getState().setExercises;

    const types = useExercisePrintStore.getState().questionOptions;
    const difficulty = useExercisePrintStore.getState().difficulty;
    const favorites = useExercisePrintStore.getState().favorites;
    const subject = useExerciseStore.getState().active;
    const unanswered = useExercisePrintStore.getState().unanswered;

    setLoading(true);

    const subjects = JSON.stringify([subject?.slug]);
    const difficulties = JSON.stringify(difficulty);
    const questionsArray = JSON.stringify(types);

    let url = `/exercises/new/print?page=1`;

    url = url + `&difficulty=${difficulties}`;
    url = url + `&subject=${subjects}`;
    url = url + `&subarea=${subarea}`;
    url = url + `&questionOptions=${questionsArray}`;

    if (favorites) {
      url = url + `&favorites=${favorites}`;
    }

    if (unanswered) {
      url = url + `&unanswered=${unanswered}`;
    }

    const { data } = await axiosInstance.get(url);

    setExercises(data);
    setLoading(false);

    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}
